import Earth16 from '@carbon/icons-react/es/earth/16';
import Group16 from '@carbon/icons-react/es/group/16';
import Search16 from '@carbon/icons-react/es/search/16';
import UserAvatar16 from '@carbon/icons-react/es/user--avatar/16';
import User16 from '@carbon/icons-react/es/user/16';
import { Tooltip } from 'antd';
import React, { FC, useCallback, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useHistory } from 'react-router';
import { Dashboard } from 'api/dashboard';
import { Input } from 'components/atoms/input';
import { confirm } from 'components/atoms/modal';
import {
  Content,
  Header,
  Menu,
  SearchInput,
  Sider,
} from 'components/molecules/views/saved-views/components';
import { useTranslate, useTranslation } from 'hooks/use-translation';
import { useUser } from 'hooks/use-user';
import { useDeleteDashboardMutation } from 'queries/dashboard';
import { useGetDashboardUrlWithSearch } from 'utils/urls';
import { useDashboardToastMessage } from '../common';
import {
  DashboardModal,
  DashboardModelVariant,
  useDashboardManageModal,
} from '../dashboard-modal';
import { useDashboardInfo } from '../useDashboardInfo';
import {
  ContextMenuKeys,
  ItemGroupHeader,
  MenuItem,
  renderDefaultOptions,
  renderSubMenu,
} from './components';

const renderTitle = (title, value?: any) => (
  <FormattedMessage id={title} values={value} />
);
const sharedIcon = (
  <Tooltip title={<FormattedMessage id="views.view_shared_by_me.tooltip" />}>
    <Group16 />
  </Tooltip>
);
const defaultIcon = (
  <Tooltip title={<FormattedMessage id="views.view_shared_by_me.tooltip" />}>
    <Earth16 />
  </Tooltip>
);
const onlyMeIcon = (
  <Tooltip title={<FormattedMessage id="views.view_only_for_me.tooltip" />}>
    <UserAvatar16 />
  </Tooltip>
);

interface Props {
  isShown: boolean;
  setShown: (toggle: boolean) => void;
}

export const DashboardNav: FC<Props> = ({ isShown, setShown }) => {
  const translate = useTranslate();
  const { mutateAsync: deleteMutation } = useDeleteDashboardMutation();
  const toastAction = useDashboardToastMessage();
  const [searchText, setSearchText] = useState('');
  const {
    variant,
    modalProps,
    modalActions: { setModalClose, setModalOpen },
  } = useDashboardManageModal();
  const {
    selectedDashboard: { selectedId: currentId },
    allDashboards: dashboards,
    isLoading: loading,
  } = useDashboardInfo();
  const getDashboardUrl = useGetDashboardUrlWithSearch();
  const history = useHistory();
  const user = useUser();
  const currentUserId = user.userMeta.userId;

  const onClick = useCallback(
    (dashboard: Dashboard) => {
      const { dashboardId } = dashboard;

      history.push(getDashboardUrl(dashboardId));
    },
    [getDashboardUrl, history],
  );

  const onDelete = useCallback(
    (dashboard: Dashboard) => {
      confirm({
        okText: translate('delete'),
        okType: 'danger',
        cancelText: translate('cancel'),
        onCancel: () => {},
        title: translate('views.delete_confirmation.title'),
        content: translate('dashboard.delete_confirmation.content', {
          name: dashboard.dashboardName,
          strong: (chunks) => <strong>{chunks}</strong>,
        }),
        onOk: () => {
          deleteMutation(dashboard.dashboardId)
            .then(() => {
              toastAction.showDeletedMessage(dashboard.dashboardName);
            })
            .catch(() => {
              toastAction.showDeletedMessage(dashboard.dashboardName, false);
            });
        },
      });
    },
    [deleteMutation, toastAction, translate],
  );
  const onEdit = useCallback(
    (dashboard: Dashboard) => {
      setModalOpen(DashboardModelVariant.Edit, dashboard);
    },
    [setModalOpen],
  );
  const onDuplicate = useCallback(
    (dashboard: Dashboard) => {
      setModalOpen(DashboardModelVariant.Duplicate, dashboard);
    },
    [setModalOpen],
  );

  const sharedWithMe = useCallback(
    (dashboard: Dashboard) => dashboard.createdBy !== currentUserId,
    [currentUserId],
  );
  const sharedByMe = useCallback(
    (dashboard: Dashboard) =>
      dashboard.createdBy === currentUserId && dashboard.shared,
    [currentUserId],
  );
  const savedByMe = useCallback(
    (dashboard: Dashboard) => dashboard.createdBy === currentUserId,
    [currentUserId],
  );
  const filterBySearch = useCallback(
    (d: Dashboard) =>
      d.dashboardName.toLowerCase().includes(searchText.toLowerCase()),
    [searchText],
  );

  const itemIcon = useCallback(
    (dashboard: Dashboard) =>
      dashboard.isDefault
        ? defaultIcon
        : sharedWithMe(dashboard)
        ? null
        : sharedByMe(dashboard)
        ? sharedIcon
        : onlyMeIcon,
    [sharedByMe, sharedWithMe],
  );

  const getContextMenuKey = useCallback(
    (d: Dashboard) => {
      if (d.isDefault) {
        return [ContextMenuKeys.Edit, ContextMenuKeys.Duplicate];
      }
      if (d.createdBy === currentUserId) {
        return [
          ContextMenuKeys.Edit,
          ContextMenuKeys.Duplicate,
          ContextMenuKeys.Delete,
        ];
      }

      return [ContextMenuKeys.Duplicate];
    },
    [currentUserId],
  );

  const transformToMenuItem = useCallback(
    (d: Dashboard): MenuItem<Dashboard> => ({
      item: d,
      name: d.dashboardName,
      icon: itemIcon(d),
      id: d.dashboardId,
      shouldShowMenu: savedByMe(d),
      contextMenu: getContextMenuKey(d),
    }),
    [getContextMenuKey, itemIcon, savedByMe],
  );

  const menuItem = useMemo(() => {
    const defaultDashboard = dashboards.find((d) => d.isDefault);

    return {
      savedByMe: dashboards
        .filter(savedByMe)
        .filter((d) => !d.isDefault)
        .map(transformToMenuItem),
      sharedWithMe: dashboards
        .filter(sharedWithMe)
        .filter((d) => !d.isDefault)
        .map(transformToMenuItem),
      default: defaultDashboard ? transformToMenuItem(defaultDashboard) : null,
    };
  }, [dashboards, savedByMe, sharedWithMe, transformToMenuItem]);

  const searchDashboard = useMemo(
    () => dashboards.filter(filterBySearch).map(transformToMenuItem),
    [dashboards, filterBySearch, transformToMenuItem],
  );

  return (
    <>
      <Sider collapsed={!isShown} onClose={() => setShown(false)}>
        <Header>
          <FormattedMessage id="dashboard.slider.title" />
        </Header>
        <SearchInput>
          <Input
            placeholder={useTranslation('dashboard.slider.search.placeholder')}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            disabled={loading}
          />
          <Search16 />
        </SearchInput>
        <Content>
          <Menu
            mode="inline"
            defaultOpenKeys={Object.values(ItemGroupHeader)}
            selectedKeys={[currentId.toString()]}
          >
            {searchText ? (
              renderSubMenu({
                key: ItemGroupHeader.SearchResult,
                menuItems: searchDashboard,
                onClick,
                onDelete,
                onEdit,
                onDuplicate,
                loading,
                label: renderTitle('views.searched_views.title', {
                  count: dashboards.filter(filterBySearch).length,
                }),
                icon: <Group16 />,
              })
            ) : (
              <>
                {menuItem.default &&
                  renderDefaultOptions({
                    menuItem: menuItem.default,
                    onClick,
                    onEdit,
                    onDuplicate,
                    loading: !menuItem.default,
                  })}
                {renderSubMenu({
                  key: ItemGroupHeader.SavedItems,
                  icon: <User16 />,
                  menuItems: menuItem.savedByMe,
                  onClick,
                  onDelete,
                  onEdit,
                  onDuplicate,
                  loading,
                  label: renderTitle('dashboard.slider.saved'),
                })}
                {renderSubMenu({
                  icon: <Group16 />,
                  key: ItemGroupHeader.SharedItems,
                  menuItems: menuItem.sharedWithMe,
                  onClick,
                  onDelete,
                  onEdit,
                  onDuplicate,
                  loading,
                  label: renderTitle('dashboard.slider.shared'),
                })}
              </>
            )}
          </Menu>
        </Content>
      </Sider>
      <DashboardModal
        variant={variant}
        onClose={setModalClose}
        {...modalProps}
      />
    </>
  );
};
